
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import type { FormData } from '../types';
import type { CrystalBallFormData } from '@/components/crystal-ball/types';

export const useRateWhispererForm = () => {
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [crystalBallMode, setCrystalBallMode] = useState(false);
  const [promptMode, setPromptMode] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    salary: "",
    bonus: "",
    expenses: {
      internetPhone: "",
      techSoftware: "",
      workspaceOffice: "",
      professionalDev: "",
      healthcare: "",
      transportation: "",
    },
    timeOff: {
      vacation: "",
      sick: "",
      holidays: "",
    },
    productivityRate: "80"
  });

  const [crystalBallData, setCrystalBallData] = useState<CrystalBallFormData>({
    desiredHourlyRate: "",
    projectHours: "",
    complexityFactor: "1.0",
    billableHoursPerDay: "6",
    expenses: {
      internetPhone: "",
      techSoftware: "",
      workspaceOffice: "",
      professionalDev: "",
      healthcare: "",
      transportation: "",
    },
    timeOff: {
      vacation: "",
      sick: "",
      holidays: "",
    }
  });

  const handleInputChange = (field: string, value: string, category?: 'expenses' | 'timeOff') => {
    if (category === 'timeOff') {
      if (value === '' || /^\d*$/.test(value)) {
        if (crystalBallMode) {
          setCrystalBallData(prev => ({
            ...prev,
            timeOff: {
              ...prev.timeOff,
              [field]: value
            }
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            timeOff: {
              ...prev.timeOff,
              [field]: value
            }
          }));
        }
      }
      return;
    }

    if (crystalBallMode) {
      setCrystalBallData(prev => {
        if (category) {
          return {
            ...prev,
            [category]: {
              ...prev[category],
              [field]: value
            }
          };
        }
        return {
          ...prev,
          [field]: value
        };
      });
    } else {
      setFormData(prev => {
        if (category) {
          return {
            ...prev,
            [category]: {
              ...prev[category],
              [field]: value
            }
          };
        }
        return {
          ...prev,
          [field]: value
        };
      });
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (crystalBallMode && !crystalBallData.desiredHourlyRate) {
        toast({
          description: "Please enter your desired hourly rate",
          variant: "destructive",
        });
        return;
      } else if (!crystalBallMode && !formData.salary) {
        toast({
          description: "Please enter your salary",
          variant: "destructive",
        });
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, crystalBallMode ? 5 : 4));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleResultsFromPrompt = (extractedFormData: FormData) => {
    setFormData(extractedFormData);
    setCurrentStep(4); // Jump directly to results
  };

  const resetForm = () => {
    if (crystalBallMode) {
      setCrystalBallData({
        desiredHourlyRate: "",
        projectHours: "",
        complexityFactor: "1.0",
        billableHoursPerDay: "6",
        expenses: {
          internetPhone: "",
          techSoftware: "",
          workspaceOffice: "",
          professionalDev: "",
          healthcare: "",
          transportation: "",
        },
        timeOff: {
          vacation: "",
          sick: "",
          holidays: "",
        }
      });
    } else {
      setFormData({
        salary: "",
        bonus: "",
        expenses: {
          internetPhone: "",
          techSoftware: "",
          workspaceOffice: "",
          professionalDev: "",
          healthcare: "",
          transportation: "",
        },
        timeOff: {
          vacation: "",
          sick: "",
          holidays: "",
        },
        productivityRate: "80"
      });
    }
    setCurrentStep(1);
  };

  return {
    currentStep,
    crystalBallMode,
    promptMode,
    formData,
    crystalBallData,
    setCrystalBallMode,
    setPromptMode,
    setFormData,
    setCrystalBallData,
    setCurrentStep,
    handleInputChange,
    handleNext,
    handleBack,
    handleResultsFromPrompt,
    resetForm,
  };
};
